import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { MessageService } from 'primeng/api';
import {
    BehaviorSubject,
    forkJoin,
    Observable,
    of,
    Subscription
} from 'rxjs';
import { catchError, finalize, map, mapTo, shareReplay, switchMap, tap } from 'rxjs/operators';

import { DistributionApi } from '../../../api/distributions/distribution.api';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { CommonApiErrorModel } from '../../../api/genericApi/models/common-api-error.model';
import { UserApplicationPreferencesApi } from '../../../api/userApplicationPreferences/userApplicationPreferences.api';
import { UsersApi } from '../../../api/users/users.api';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { FooterModel } from '../../../Common/TargetingShared/model/footer.model';
import { LanguageTagEnum } from '../../../Core/Localization/enums/language-tag.enum';
import { DataLanguageModel } from '../../../Core/Users/models/data-language.model';
import { PreferencesModel } from '../../../Core/Users/models/preferences.model';
import {
    UserAppPreferencesModel
} from '../../../Core/Users/models/user-app-preferences.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';

export enum UserAppMessageTypeEnum {
    GENERIC = 'GENERIC',
    EXPORT = 'EXPORT',
    RESET = 'RESET',
}

@Injectable()
export class UserPreferencesService {
    private subscriptions: Subscription[] = [];
    private hasExistingUserAppPreferences: boolean = false;

    private uiLanguages: DataLanguageModel[] = null;
    private uiLanguages$ = new BehaviorSubject<DataLanguageModel[]>(this.uiLanguages);

    private referenceDataLanguages: DataLanguageModel[] = null;
    private referenceDataLanguages$ = new BehaviorSubject<DataLanguageModel[]>(this.referenceDataLanguages);

    private userAppPreferences$: Observable<UserAppPreferencesModel>;
    private updateUserAppPreferences$ = new BehaviorSubject<void>(undefined);

    defaultDistributionFooter: FooterModel = new FooterModel();
    originalDefaultDistributionFooter: FooterModel;
    private defaultDistributionFooter$: Observable<FooterModel>;
    private refreshDistributionFooter$ = new BehaviorSubject<void>(undefined);
    referenceDataLanguageOptions: DropdownOptionModel[] = [];

    private loadingSubject$ = new BehaviorSubject<boolean>(false);
    loading$ = this.loadingSubject$.asObservable();

    constructor(
        private usersApi: UsersApi,
        private messageService: MessageService,
        private distributionApi: DistributionApi,
        private userAppPrefsApi: UserApplicationPreferencesApi
    ) {
        this.defaultDistributionFooter$ = this.refreshDistributionFooter$.pipe(
            switchMap(() => this.distributionApi.getDefaultFooter().pipe(
                map(footer => {
                    this.defaultDistributionFooter = new FooterModel(footer);
                    this.originalDefaultDistributionFooter = JSON.parse(JSON.stringify(this.defaultDistributionFooter));
                    return this.defaultDistributionFooter;
                }),
            ))
        ).pipe(
            shareReplay(1),
        );

        this.userAppPreferences$ = this.updateUserAppPreferences$.pipe(
            switchMap(() => this.userAppPrefsApi.getCurrentUserAppPrefs().pipe(
                tap(() => this.hasExistingUserAppPreferences = true),
                map(userAppPrefsApiModel => new UserAppPreferencesModel(userAppPrefsApiModel)),
                catchError((error: HttpErrorResponse) => {
                    if (CommonApiErrorModel.getErrorDetail(error.error)?.code === CommonApiErrorEnum.badReference) {
                        this.hasExistingUserAppPreferences = false;
                        // UserAppPreferences don't exist yet for this user, use defaults
                        return of(new UserAppPreferencesModel());
                    }
                    console.error('Could not retrieve User Application Preferences', error);

                    // it should always at a minimum return the default user prefs.. else the app crashes
                    return of(new UserAppPreferencesModel());
                    // TODO: Other error handling
                })
            ))
        ).pipe(
            shareReplay(1),
        );
    }

    /**
     * GENERAL PREFS REGION
     */
    private setLanguageLookupValues() {
        this.usersApi.getPreferenceLookupValues().subscribe(result => {
            if (result) {
                this.uiLanguages = result.uiLanguages;
                this.uiLanguages$.next(this.uiLanguages);
                this.referenceDataLanguages = result.referenceDataLanguages;
                this.referenceDataLanguages$.next(this.referenceDataLanguages);
            }
        });
    }

    public getUILanguages(): Observable<DataLanguageModel[]> {
        if (!this.uiLanguages) {
            this.setLanguageLookupValues();
        }
        return this.uiLanguages$;
    }

    public getReferenceDataLanguages(): Observable<DataLanguageModel[]> {
        if (!this.referenceDataLanguages) {
            this.setLanguageLookupValues();
        }
        return this.referenceDataLanguages$;
    }

    public updateGeneralPreferences(preference: PreferencesModel, noGrowl = false): Observable<null> {
        return this.usersApi.updateGeneralPreferences(preference.toUpdateApiModel()).pipe(
            tap(() => {
                if (!noGrowl){
                    this.showSuccessMessage();
                }
            })
        );
    }

    getUserAppPreferences(): Observable<UserAppPreferencesModel> {
        return this.userAppPreferences$;
    }

    /**
     * DISCOVER PREFS REGION
     */
    getDistributionDefaultFooter(): Observable<FooterModel> {
        return this.defaultDistributionFooter$;
    }

    refreshDistributionDefaultFooter() {
        this.refreshDistributionFooter$.next();
    }

    saveChanges(newUserAppPrefs: UserAppPreferencesModel, messageType: UserAppMessageTypeEnum = UserAppMessageTypeEnum.GENERIC): Observable<void> {
        const apiCalls: Observable<any>[] = [];
        if (this.hasExistingUserAppPreferences) {
            apiCalls.push(this.userAppPrefsApi.updateCurrentUserAppPrefs(newUserAppPrefs.toApiModel()));
        } else {
            apiCalls.push(this.userAppPrefsApi.createCurrentUserAppPrefs(newUserAppPrefs.toApiModel()));
        }

        if (JSON.stringify(this.defaultDistributionFooter) !== JSON.stringify(this.originalDefaultDistributionFooter)) {
            apiCalls.push(
                this.distributionApi.setDefaultFooter(this.defaultDistributionFooter.toApiModel(null)).pipe(
                    tap(() => this.refreshDistributionDefaultFooter())
                )
            );
        };

        this.loadingSubject$.next(true);
        return forkJoin(apiCalls).pipe(
            tap(() => this.showSuccessMessage(messageType)),
            tap(() => this.hasExistingUserAppPreferences = true),
            tap(() => this.updateUserAppPreferences$.next()),
            mapTo(undefined),
            catchError(error => {
                this.showErrorMessage(messageType);
                return of(undefined);
            }),
            finalize(() => this.loadingSubject$.next(false))
        );
    }

    forceGetUserPrefs() {
        this.updateUserAppPreferences$.next();
    }

    getDefaultLanguageOptions(): { uiLanguage: DataLanguageModel; referenceDataLanguage: DataLanguageModel } {
        return {
            uiLanguage: this.uiLanguages?.find(option => option.languageTag === LanguageTagEnum.en_US),
            referenceDataLanguage: this.referenceDataLanguages?.find(option => option.languageTag === LanguageTagEnum.en_US),
        };
    }


    showSuccessMessage(messageType: UserAppMessageTypeEnum = UserAppMessageTypeEnum.GENERIC) {
        if (messageType === UserAppMessageTypeEnum.EXPORT) {
            this.messageService.add({
                severity: messageSeverityEnum.success,
                detail: 'UserPreferences.export.success.message',
                summary: 'UserPreferences.export.success.heading',
            });
        } else if (messageType === UserAppMessageTypeEnum.RESET) {
            this.messageService.add({
                severity: messageSeverityEnum.success,
                detail: 'UserPreferences.reset.success.message',
                summary: 'UserPreferences.reset.success.heading',
            });
        } else {
            this.messageService.add({
                severity: messageSeverityEnum.success,
                detail: 'UserPreferences.update.success.message',
                summary: 'UserPreferences.update.success.heading',
            });
        }
    }

    showErrorMessage(messageType: UserAppMessageTypeEnum = UserAppMessageTypeEnum.GENERIC) {
        if (messageType === UserAppMessageTypeEnum.EXPORT) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                detail: 'UserPreferences.export.error.message',
                summary: 'UserPreferences.export.error.heading',
            });
        } else if (messageType === UserAppMessageTypeEnum.RESET) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                detail: 'UserPreferences.reset.error.message',
                summary: 'UserPreferences.reset.error.heading',
            });
        } else {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                detail: 'UserPreferences.update.error.message',
                summary: 'UserPreferences.update.error.heading',
            });
        }
    }
}
